<template>
  <div class="reviews-page">
    <!-- Background Image -->
    <div class="page-background"></div>
    <!-- Page Header -->
    <div class="d-flex justify-content-between align-items-center mb-4">
      <div>
        <h1 class="h3 mb-1">Manage Reviews</h1>
        <p class="text-muted mb-0">Create and manage customer testimonials</p>
      </div>
      <router-link to="/admin/reviews/create" class="btn btn-primary">
        <i class="fas fa-plus me-2"></i>
        Add New Review
      </router-link>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="mt-3 text-muted">Loading reviews...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
      <button @click="loadReviews" class="btn btn-sm btn-outline-danger ms-2">
        <i class="fas fa-redo me-1"></i>
        Retry
      </button>
    </div>

    <!-- Empty State -->
    <div v-else-if="allReviews.length === 0" class="text-center py-5">
      <i class="fas fa-star fa-4x text-muted mb-4"></i>
      <h4>No reviews found</h4>
      <p class="text-muted mb-4">Start by adding your first customer review</p>
      <router-link to="/admin/reviews/create" class="btn btn-primary">
        <i class="fas fa-plus me-2"></i>
        Add First Review
      </router-link>
    </div>

    <!-- Reviews Scrollable Row -->
    <div v-else class="reviews-scroll-container">
      <div class="reviews-scroll-row">
        <div
          v-for="review in allReviews"
          :key="review.id"
          class="review-card-wrapper"
        >
          <div class="card review-card h-100">
            <!-- Review Header -->
            <div
              class="card-header d-flex justify-content-between align-items-center"
            >
              <div class="reviewer-info">
                <h6 class="mb-0">{{ review.reviewerName }}</h6>
                <div class="rating">
                  <i
                    v-for="n in 5"
                    :key="n"
                    :class="
                      n <= review.rating
                        ? 'fas fa-star text-warning'
                        : 'far fa-star text-muted'
                    "
                  ></i>
                </div>
              </div>
              <div class="review-actions dropdown">
                <button
                  class="btn btn-sm btn-outline-secondary dropdown-toggle"
                  type="button"
                  data-bs-toggle="dropdown"
                >
                  <i class="fas fa-ellipsis-v"></i>
                </button>
                <ul class="dropdown-menu">
                  <li>
                    <router-link
                      :to="`/admin/reviews/${review.id}/edit`"
                      class="dropdown-item"
                    >
                      <i class="fas fa-edit me-2"></i>
                      Edit
                    </router-link>
                  </li>
                  <li>
                    <button
                      @click="deleteReview(review)"
                      class="dropdown-item text-danger"
                    >
                      <i class="fas fa-trash me-2"></i>
                      Delete
                    </button>
                  </li>
                </ul>
              </div>
            </div>

            <div class="card-body d-flex flex-column">
              <!-- Review Text (always show if present) -->
              <div
                v-if="review.reviewText && review.reviewText.trim()"
                class="flex-grow-1 mb-3"
              >
                <p
                  class="review-text"
                  :class="{
                    'text-truncated': isTextTruncated(review.reviewText),
                  }"
                >
                  {{ getTruncatedText(review.reviewText, review.id) }}
                </p>
                <button
                  v-if="isTextTruncated(review.reviewText)"
                  @click="toggleTextExpansion(review.id)"
                  class="btn btn-link btn-sm p-0 text-primary see-more-btn"
                >
                  {{ expandedReviews[review.id] ? "See less" : "See more" }}
                </button>
              </div>

              <!-- Review Images (show below text if present) -->
              <div
                v-if="review.images && review.images.length > 0"
                class="review-media mb-3"
              >
                <div class="media-scroll-container">
                  <div class="media-scroll-row">
                    <div
                      v-for="(imageUrl, index) in review.images"
                      :key="`img-${index}`"
                      class="media-item"
                      @click="openMediaInNewTab(imageUrl)"
                    >
                      <img
                        :src="imageUrl"
                        :alt="`Review image ${index + 1}`"
                        class="media-thumbnail"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Review Video (show below text and images if present) -->
              <div v-if="review.video" class="review-media mb-3">
                <div class="media-scroll-container">
                  <div class="media-scroll-row">
                    <div
                      class="media-item"
                      @click="openMediaInNewTab(review.video)"
                    >
                      <video
                        :src="review.video"
                        class="media-thumbnail"
                        muted
                      ></video>
                      <!-- Video play indicator -->
                      <div class="video-play-overlay">
                        <i class="fas fa-play"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Review Footer -->
              <div class="review-footer">
                <small class="text-muted">
                  <i class="fas fa-calendar me-1"></i>
                  {{ formatDate(review.createdAt) }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Media Viewer Modal -->
    <MediaViewer
      v-if="selectedMedia"
      :media="selectedMedia"
      :initial-index="selectedMediaIndex"
      @close="selectedMedia = null"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import moment from "moment";
import Swal from "sweetalert2";
import MediaViewer from "@/components/MediaViewer.vue";

export default {
  name: "Reviews",
  components: {
    MediaViewer,
  },
  data() {
    return {
      selectedMedia: null,
      selectedMediaIndex: 0,
      expandedReviews: {},
      maxTextLength: 150,
    };
  },
  computed: {
    ...mapGetters("reviews", ["allReviews", "isLoading", "error"]),
  },
  methods: {
    ...mapActions("reviews", ["fetchReviews", "deleteReview"]),

    formatDate(dateString) {
      return moment(dateString).format("MMM DD, YYYY");
    },

    async loadReviews() {
      try {
        await this.fetchReviews();
      } catch (error) {
        console.error("Error loading reviews:", error);
      }
    },

    async deleteReview(review) {
      try {
        const result = await Swal.fire({
          title: "Delete Review",
          text: `Are you sure you want to delete the review by "${review.reviewerName}"? This action cannot be undone.`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#dc3545",
          cancelButtonColor: "#6c757d",
          confirmButtonText: "Yes, delete it!",
          cancelButtonText: "Cancel",
        });

        if (result.isConfirmed) {
          await this.$store.dispatch("reviews/deleteReview", review.id);

          Swal.fire({
            title: "Deleted!",
            text: "Review has been deleted successfully.",
            icon: "success",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          confirmButtonColor: "#2c5aa0",
        });
      }
    },

    viewMedia(media, index) {
      this.selectedMedia = media;
      this.selectedMediaIndex = index;
    },

    isImage(url) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
    },

    isVideo(url) {
      return /\.(mp4|avi|mov|wmv|flv|webm)$/i.test(url);
    },

    isTextTruncated(text) {
      return text && text.length > this.maxTextLength;
    },

    getTruncatedText(text, reviewId) {
      if (!text) return "";
      if (this.expandedReviews[reviewId] || !this.isTextTruncated(text)) {
        return text;
      }
      return text.substring(0, this.maxTextLength) + "...";
    },

    toggleTextExpansion(reviewId) {
      this.expandedReviews[reviewId] = !this.expandedReviews[reviewId];
    },

    openMediaInNewTab(mediaUrl) {
      window.open(mediaUrl, "_blank");
    },
  },
  async created() {
    await this.loadReviews();
  },
};
</script>

<style scoped>
.reviews-page {
  animation: fadeIn 0.5s ease-out;
  position: relative;
  min-height: 100vh;
}

.page-background {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url("@/assets/wooden.jpg");
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.1;
  z-index: -1;
}

.review-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.review-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.reviewer-info h6 {
  color: var(--dark-color);
  font-weight: 600;
}

.rating {
  margin-top: 0.25rem;
}

.rating i {
  font-size: 0.9rem;
  margin-right: 0.1rem;
}

.review-text {
  line-height: 1.5;
  color: var(--dark-color);
  font-size: 0.95rem;
  /* Ensure emojis render correctly */
  font-family: "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji", "Android Emoji", "EmojiSymbols", "EmojiOne Mozilla", "Twemoji Mozilla", "Segoe UI", "Helvetica Neue", Arial, sans-serif;
  font-feature-settings: "liga" off;
  -webkit-font-feature-settings: "liga" off;
  -moz-font-feature-settings: "liga" off;
  text-rendering: optimizeLegibility;
}

.media-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.media-item,
.media-more {
  aspect-ratio: 1;
  border-radius: var(--border-radius);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.media-item:hover,
.media-more:hover {
  transform: scale(1.05);
}

.media-thumbnail {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.media-more {
  background: rgba(0, 0, 0, 0.7);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.1rem;
}

/* Scrollable Media Container */
.media-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
}

.media-scroll-container::-webkit-scrollbar {
  height: 6px;
}

.media-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.media-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.media-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.media-scroll-row {
  display: flex;
  gap: 0.75rem;
  min-width: max-content;
  padding: 4px 0;
}

.media-scroll-row .media-item {
  flex-shrink: 0;
  width: 250px;
  height: 250px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  position: relative;
  border: 2px solid transparent;
}

.media-scroll-row .media-item:hover {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-color: var(--primary-color);
}

.video-play-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  transition: background 0.3s ease;
}

.media-scroll-row .media-item:hover .video-play-overlay {
  background: rgba(0, 0, 0, 0.8);
}

/* Text truncation and see more */
.review-text {
  line-height: 1.5;
  color: var(--dark-color);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.text-truncated {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.see-more-btn {
  font-size: 0.8rem;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.see-more-btn:hover {
  color: var(--secondary-color) !important;
  text-decoration: none;
}

/* Reviews Scrollable Container */
.reviews-scroll-container {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 12px;
  margin-top: 1rem;
}

.reviews-scroll-container::-webkit-scrollbar {
  height: 8px;
}

.reviews-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.reviews-scroll-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.reviews-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.reviews-scroll-row {
  display: flex;
  gap: 1.5rem;
  min-width: max-content;
  padding: 8px 0;
}

.review-card-wrapper {
  flex-shrink: 0;
  width: 280px;
  min-height: 320px;
}

.review-footer {
  border-top: 1px solid #dee2e6;
  padding-top: 0.75rem;
  margin-top: 0.75rem;
}

@media (max-width: 768px) {
  .media-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .review-actions .dropdown-toggle {
    padding: 0.25rem 0.5rem;
  }
}
</style>
